import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from "@angular/core";

// The table wrapper scrolls horizontally, so it clips absolutely positioned children: a .popup-menu
// left inside its row gets cut off. When the menu opens it is moved into a zero-height layer at the
// top of .nec-content-container (the list page's vertical scroller, below the main header) and
// positioned there against its trigger, the clicked .dots icon (not the wrapper, whose dots-padding
// would push it far below the icon). Living inside the scroller, it scrolls with the rows natively,
// with no lag, and can't cover the main header. Placement: just below the icon, its right edge a
// little left of the icon's, kept inside the list horizontally. Whether it opens above or below is
// decided once, on open (above only when there isn't room below in the list and there is more room
// above); a window resize decides again, and any other scroll (e.g. the table's horizontal scroll)
// only re-follows the icon on the same side.
//
// The host app's "anti-zoom" sets a CSS `zoom` on :root that depends on the screen's device pixel
// ratio (e.g. 0.8 at 125% scaling). getBoundingClientRect() is in on-screen pixels while top/left are
// applied in the menu's own (zoomed) CSS pixels, so on-screen distances are divided by the menu's
// effective zoom before they are used.
@Directive({
  selector: "[libPopupMenuFlip]",
  standalone: false,
})
export class PopupMenuFlipDirective implements AfterViewInit, OnDestroy {
  private layer: HTMLElement | null = null;
  private trigger: Element | null = null;
  private openUp = false;
  private readonly followTrigger = () => this.position(false);
  private readonly replace = () => this.position(true);

  constructor(private elementRef: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    const menu = this.elementRef.nativeElement;
    const wrapper = menu.parentElement!;
    const scroller = menu.closest(".nec-content-container");
    if (!scroller) return;

    this.trigger = wrapper.querySelector(".dots") ?? wrapper;
    this.layer = document.createElement("div");
    this.layer.style.position = "relative";
    this.layer.style.height = "0";
    scroller.prepend(this.layer);
    this.layer.appendChild(menu);

    this.position(true);
    this.zone.runOutsideAngular(() => {
      window.addEventListener("scroll", this.followTrigger, true);
      window.addEventListener("resize", this.replace);
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener("scroll", this.followTrigger, true);
    window.removeEventListener("resize", this.replace);
    this.layer?.remove();
  }

  private position(chooseSide: boolean): void {
    if (!this.layer || !this.trigger) return;

    const menu = this.elementRef.nativeElement;
    const zoom = menu.getBoundingClientRect().width / menu.offsetWidth || 1;
    const trigger = this.trigger.getBoundingClientRect();
    const layer = this.layer.getBoundingClientRect();
    const list = this.layer.parentElement!.getBoundingClientRect();

    // Everything below is in the menu's CSS pixels, relative to the layer.
    const triggerTop = (trigger.top - layer.top) / zoom;
    const triggerBottom = (trigger.bottom - layer.top) / zoom;
    const triggerRight = (trigger.right - layer.left) / zoom;
    const listTop = (list.top - layer.top) / zoom;
    const listBottom = (list.bottom - layer.top) / zoom;
    const listLeft = (list.left - layer.left) / zoom;
    const listRight = (list.right - layer.left) / zoom;
    const width = menu.offsetWidth;
    const height = menu.offsetHeight;
    const gap = -4;
    const shiftLeft = 10;
    const edge = 8;

    if (chooseSide) {
      const spaceBelow = listBottom - triggerBottom;
      const spaceAbove = triggerTop - listTop;
      this.openUp = spaceBelow < height + gap && spaceAbove > spaceBelow;
    }

    const top = this.openUp ? triggerTop - height - gap : triggerBottom + gap;
    const left = Math.min(Math.max(triggerRight - shiftLeft - width, listLeft + edge), listRight - width - edge);

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }
}
